/**
 * Per-session Zustand store: the source of truth for the cross-section field
 * working set + evaluation `ctx`.
 *
 * - `event` / `plan` live in the global `values` map (one of each per session).
 * - `promo` is PER INSTANCE (one working set + setter + `hasEntityId` per open
 *   promo tab) so multiple promo tabs cannot clobber each other. Each instance is
 *   evaluated against a MERGED view (global `event`/`plan` values + that
 *   instance's `promo:*`), so cross-section rules (e.g. event -> promo) still work.
 *
 * `setValues` / `setPromoValues` merge a scope's changes, run reactive rules, and
 * route value mutations to the owning form's registered setter (or persist them
 * for unmounted scopes). Pure-engine functions do the actual rule work.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import { subscribeWithSelector } from 'zustand/middleware';

import type { QualifiedId, RuleSet, Scope } from '../../config/fieldRulesConfig';
import type { ComputeHelpers, OptionResolvers, RuleContext, RuleValues } from '../types';
import { applyFieldReactions, resolveAutoFillValue } from '../engine/applyFieldReactions';
import { evaluateCondition } from '../engine/evaluateCondition';

export type ScopeSetter = (vals: Record<string, unknown>) => void;

const PROMO: Scope = 'promo';

/** One promo tab's isolated working set + its per-instance `hasEntityId` ctx bit. */
export interface PromoInstanceState {
    values: RuleValues;
    hasEntityId?: boolean;
}

export interface RuleEngineState {
    /** Global working set for `event` / `plan`, keyed by qualified id. */
    values: RuleValues;
    /** Per-promo-instance working sets (keyed by instance id, e.g. the tab key). */
    promo: Record<string, PromoInstanceState>;
    /** Global evaluation context (mode, isEdit, planYear). `hasEntityId` is per promo instance. */
    ctx: RuleContext;
    /** The active rule registry (from useTpoGenConfig().fieldRules). */
    rules: RuleSet;

    setValues: (scope: Scope, changed: Record<string, unknown>, opts?: { source?: 'user' | 'hydrate' }) => void;
    setContext: (patch: Partial<RuleContext>) => void;
    registerSetter: (scope: Scope, setter: ScopeSetter) => () => void;
    setRuntime: (runtime: { resolvers?: OptionResolvers; helpers?: ComputeHelpers }) => void;
    resetScope: (scope: Scope, fields?: string[]) => void;
    runCompute: (target: QualifiedId) => void;
    getScopeValues: (scope: Scope) => Record<string, unknown>;

    // --- Per-promo-instance API ---
    /** Bare-keyed values for one promo instance. */
    getPromoValues: (instanceId: string) => Record<string, unknown>;
    /** Merge a promo instance's changed values, then run that instance's reactions. */
    setPromoValues: (
        instanceId: string,
        changed: Record<string, unknown>,
        opts?: { source?: 'user' | 'hydrate' },
    ) => void;
    registerPromoSetter: (instanceId: string, setter: ScopeSetter) => () => void;
    setPromoHasEntityId: (instanceId: string, hasEntityId: boolean) => void;
    /** Drop one promo instance's working set + setter (tab closed). */
    resetPromoInstance: (instanceId: string) => void;
    /** Drop every promo instance (new event session). */
    resetAllPromo: () => void;
    /** Clear global ctx (keeps `planYear`). */
    resetContext: () => void;
}

export type RuleStore = StoreApi<RuleEngineState>;

const qualify = (scope: Scope, name: string): QualifiedId => `${scope}:${name}` as QualifiedId;
const scopeOf = (qid: QualifiedId): Scope => qid.slice(0, qid.indexOf(':')) as Scope;
const nameOf = (qid: QualifiedId): string => qid.slice(qid.indexOf(':') + 1);

export function createRuleStore(rules: RuleSet, initial?: { values?: RuleValues; ctx?: RuleContext }): RuleStore {
    // Non-reactive runtime bits, mutated without triggering re-renders.
    const setters = new Map<Scope, ScopeSetter>();
    const promoSetters = new Map<string, ScopeSetter>();
    let resolvers: OptionResolvers = {};
    let helpers: ComputeHelpers = {};

    return createStore<RuleEngineState>()(
        subscribeWithSelector((set, get) => {
            // Writes an event/plan value into the global set + its owning form.
            const writeGlobal = (target: QualifiedId, value: unknown) => {
                set((s) => ({ values: { ...s.values, [target]: value } }));
                setters.get(scopeOf(target))?.({ [nameOf(target)]: value });
            };

            // Writes a value into one promo instance's set + its owning form. Non-promo
            // targets fall back to the global set (no such rules today, but kept general).
            const writeInstance = (instanceId: string, target: QualifiedId, value: unknown) => {
                if (scopeOf(target) !== PROMO) {
                    writeGlobal(target, value);
                    return;
                }
                set((s) => {
                    const prev = s.promo[instanceId] ?? { values: {} };
                    return {
                        promo: {
                            ...s.promo,
                            [instanceId]: { ...prev, values: { ...prev.values, [target]: value } },
                        },
                    };
                });
                promoSetters.get(instanceId)?.({ [nameOf(target)]: value });
            };

            const mergedValues = (instanceId: string): RuleValues => ({
                ...get().values,
                ...(get().promo[instanceId]?.values ?? {}),
            });
            const mergedCtx = (instanceId: string): RuleContext => ({
                ...get().ctx,
                hasEntityId: get().promo[instanceId]?.hasEntityId,
            });

            // Re-runs reactive rules whose targets are `promo` against every open promo
            // instance (e.g. an event-field change that autofills a promo field).
            const fanOutPromoReactions = (changed: RuleValues) => {
                for (const instanceId of Object.keys(get().promo)) {
                    applyFieldReactions(
                        get().rules,
                        changed,
                        mergedValues(instanceId),
                        mergedCtx(instanceId),
                        (target, value) => {
                            if (scopeOf(target) === PROMO) writeInstance(instanceId, target, value);
                        },
                        helpers,
                        resolvers,
                    );
                }
            };

            return {
                values: initial?.values ?? {},
                promo: {},
                ctx: initial?.ctx ?? {},
                rules,

                setValues: (scope, changed, opts) => {
                    const qualified: RuleValues = {};
                    for (const [key, value] of Object.entries(changed)) qualified[qualify(scope, key)] = value;

                    const prevValues = get().values;
                    set({ values: { ...prevValues, ...qualified } });

                    if (opts?.source === 'hydrate') return;
                    // Global (event/plan) reactions.
                    applyFieldReactions(
                        get().rules,
                        qualified,
                        prevValues,
                        get().ctx,
                        (target, value) => {
                            if (scopeOf(target) !== PROMO) writeGlobal(target, value);
                        },
                        helpers,
                        resolvers,
                    );
                    // Cross-section reactions that target promo, per open instance.
                    fanOutPromoReactions(qualified);
                },

                setContext: (patch) => {
                    const prev = get().ctx;
                    const next = { ...prev, ...patch };
                    set({ ctx: next });

                    const changed: RuleValues = {};
                    for (const [key, value] of Object.entries(patch)) {
                        if (prev[key] !== value) changed[`ctx:${key}`] = value;
                    }
                    if (Object.keys(changed).length > 0) {
                        applyFieldReactions(
                            get().rules,
                            changed,
                            get().values,
                            next,
                            (target, value) => {
                                if (scopeOf(target) !== PROMO) writeGlobal(target, value);
                            },
                            helpers,
                            resolvers,
                        );
                        fanOutPromoReactions(changed);
                    }
                },

                registerSetter: (scope, setter) => {
                    setters.set(scope, setter);
                    return () => {
                        if (setters.get(scope) === setter) setters.delete(scope);
                    };
                },

                setRuntime: ({ resolvers: nextResolvers, helpers: nextHelpers }) => {
                    if (nextResolvers) resolvers = nextResolvers;
                    if (nextHelpers) helpers = nextHelpers;
                },

                resetScope: (scope, fields) => {
                    if (scope === PROMO) {
                        get().resetAllPromo();
                        return;
                    }
                    const prefix = `${scope}:`;
                    const targets = fields
                        ? fields.map((f) => qualify(scope, f))
                        : (Object.keys(get().values).filter((k) => k.startsWith(prefix)) as QualifiedId[]);
                    if (targets.length === 0) return;

                    set((s) => {
                        const nextValues = { ...s.values };
                        for (const target of targets) nextValues[target] = undefined;
                        return { values: nextValues };
                    });

                    const setter = setters.get(scope);
                    if (setter) {
                        const patch: Record<string, unknown> = {};
                        for (const target of targets) patch[nameOf(target)] = undefined;
                        setter(patch);
                    }
                },

                // Manual (button-triggered) autoFill compute, e.g. "generate promo name".
                runCompute: (target) => {
                    const { rules: activeRules, values, ctx } = get();
                    for (const rule of activeRules) {
                        if (rule.effect.kind !== 'autoFill') continue;
                        const tlist = Array.isArray(rule.target) ? rule.target : [rule.target];
                        if (!tlist.includes(target)) continue;
                        if (!evaluateCondition(rule.when, values, ctx)) continue;
                        writeGlobal(target, resolveAutoFillValue(rule.effect.value, values, ctx, helpers, resolvers));
                    }
                },

                getScopeValues: (scope) => {
                    const prefix = `${scope}:`;
                    const out: Record<string, unknown> = {};
                    for (const [key, value] of Object.entries(get().values)) {
                        if (key.startsWith(prefix)) out[key.slice(prefix.length)] = value;
                    }
                    return out;
                },

                getPromoValues: (instanceId) => {
                    const out: Record<string, unknown> = {};
                    for (const [key, value] of Object.entries(get().promo[instanceId]?.values ?? {})) {
                        out[nameOf(key as QualifiedId)] = value;
                    }
                    return out;
                },

                setPromoValues: (instanceId, changed, opts) => {
                    const qualified: RuleValues = {};
                    for (const [key, value] of Object.entries(changed)) qualified[qualify(PROMO, key)] = value;

                    const prevInstance = get().promo[instanceId]?.values ?? {};
                    set((s) => {
                        const prev = s.promo[instanceId] ?? { values: {} };
                        return {
                            promo: { ...s.promo, [instanceId]: { ...prev, values: { ...prevInstance, ...qualified } } },
                        };
                    });

                    if (opts?.source === 'hydrate') return;
                    const prevMerged: RuleValues = { ...get().values, ...prevInstance };
                    applyFieldReactions(
                        get().rules,
                        qualified,
                        prevMerged,
                        mergedCtx(instanceId),
                        (target, value) => writeInstance(instanceId, target, value),
                        helpers,
                        resolvers,
                    );
                },

                registerPromoSetter: (instanceId, setter) => {
                    promoSetters.set(instanceId, setter);
                    return () => {
                        if (promoSetters.get(instanceId) === setter) promoSetters.delete(instanceId);
                    };
                },

                setPromoHasEntityId: (instanceId, hasEntityId) => {
                    set((s) => {
                        const prev = s.promo[instanceId] ?? { values: {} };
                        if (prev.hasEntityId === hasEntityId) return {};
                        return { promo: { ...s.promo, [instanceId]: { ...prev, hasEntityId } } };
                    });
                },

                resetPromoInstance: (instanceId) => {
                    promoSetters.delete(instanceId);
                    set((s) => {
                        if (!(instanceId in s.promo)) return {};
                        const next = { ...s.promo };
                        delete next[instanceId];
                        return { promo: next };
                    });
                },

                resetAllPromo: () => {
                    promoSetters.clear();
                    set({ promo: {} });
                },

                resetContext: () => {
                    set((s) => ({ ctx: s.ctx.planYear !== undefined ? { planYear: s.ctx.planYear } : {} }));
                },
            };
        }),
    );
}
